import './VanityUrlField.scss';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormControl, InputLabel, TextField as MuiTextField, InputAdornment } from '@mui/material';
import ToolTip from '../../ToolTip';
import { isWhitelabel } from 'src/utils/utils';

export default function VanityUrlField (props) {
  const {
    field,
    value,
    error,
    handleChange,
    disabled,
    showLabel,
    showHelperText,
  } = props;

  const whitelabel = isWhitelabel();
  const fieldId = field.get('id');
  const domain = whitelabel ? 'atcom.plus' : 'smarttrace.ai';
  const prefix = 'https://';
  const suffix = `.${domain}`;

  // Extract the middle part from the full URL value
  // If value is the full URL, extract just the middle part
  // If value is just the middle part (or empty), use it as is
  const middlePart = useMemo(() => {
    if (!value) return '';

    // Check if value is already a full URL
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedSuffix = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const fullUrlPattern = new RegExp(`^${escapedPrefix}(.+?)${escapedSuffix}(/.*)?$`);
    const match = value.match(fullUrlPattern);
    if (match) {
      return match[1];
    }

    // If it doesn't match the full URL pattern, assume it's just the middle part
    return value;
  }, [value, prefix, suffix]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    // Only allow alphanumeric characters and hyphens
    const sanitizedValue = inputValue.replace(/[^a-zA-Z0-9-]/g, '');
    // Construct the full URL and pass it to handleChange
    const fullUrl = sanitizedValue ? `${prefix}${sanitizedValue}${suffix}` : '';
    handleChange(fieldId, fullUrl);
  };

  return <FormControl key={fieldId} className='vanity-url-field' variant='outlined' sx={{ mb: 0.8 }}>
    {
      showLabel &&
      <div style={{ display: 'flex' }}>
        <InputLabel id={`vanity-url-field-label-${fieldId}`} data-testid={`vanity-url-field-label-${fieldId}`} error={Boolean(error)} disabled={disabled} sx={{ margin: 0, padding: 0 }}>
          {field.get('label') ?? null}
        </InputLabel>
        {field.get('toolTip', null) ? (<div style={{ marginLeft: '5px' }}><ToolTip helpText={field.get('toolTip', null)} ></ToolTip></div>) : null}
      </div>
    }
    <MuiTextField
      data-testid={`vanity-url-field-${fieldId}`}
      fullWidth
      key={`vanity-url-field-${fieldId}`}
      id={`vanity-url-field-${fieldId}`}
      name={`vanity-url-field-${fieldId}`}
      type="text"
      value={middlePart}
      onChange={handleInputChange}
      error={Boolean(error)}
      disabled={disabled}
      helperText={
        showHelperText ?
          <span data-testid={`vanity-url-field-helper-text-${fieldId}`}>
            {(error) ?
              <>
                {error}<br />
              </> : null}
            {field.get('helperText')}
          </span> : null
      }
      variant='outlined'
      InputLabelProps={{ shrink: false }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <span style={{ color: 'rgba(0, 0, 0, 0.6)', userSelect: 'none' }}>{prefix}</span>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <span style={{ color: 'rgba(0, 0, 0, 0.6)', userSelect: 'none' }}>{suffix}</span>
          </InputAdornment>
        ),
      }}
      inputProps={{
        'aria-label': field.get('label'),
        style: { textAlign: 'left' }
      }}
    />

  </FormControl>;
}

VanityUrlField.propTypes = {
  field: ImmutablePropTypes.map,
  error: PropTypes.string,
  value: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showLabel: PropTypes.bool,
  showHelperText: PropTypes.bool,
};

VanityUrlField.defaultProps = {
  disabled: false,
  showLabel: true,
  showHelperText: true,
};

VanityUrlField.whyDidYouRender = true;
